import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  exclusivePlanSelection,
  isBundlePlan,
  isLivePlan,
  isPerLocationPlan,
  planCode,
  selectionHasBundle,
} from "../../Data/billingPlans";
import { errorAlert } from "../../Stores/Alerts/actions";
import {
  changeBillingPlan,
  fetchBillingPlans,
  startBillingCheckout,
} from "../../Stores/Billing/actions";
import usePermissionAccess from "../../utils/hooks/usePermissionAccess";

const LIVE_STATUSES = ["trialing", "active", "past_due"];

const useSubscriptionPurchase = ({ refreshOn, skipFetch = false } = {}) => {
  const dispatch = useDispatch();
  const activeLocation = useSelector(
    (state) => state.SlickCareProfilesReducer.activeLocation,
  );
  const {
    plans,
    subscriptions,
    isBusy,
    isLoading,
    location: billingLocation,
  } = useSelector((state) => state.BillingReducer || {});
  const { canAccessBilling } = usePermissionAccess();
  const locationId = activeLocation?.id;
  const beds = Math.max(
    0,
    Number(billingLocation?.no_of_beds ?? activeLocation?.no_of_beds) || 0,
  );

  const refreshPlans = useCallback(() => {
    if (!locationId || !canAccessBilling) return;
    dispatch(fetchBillingPlans({ location_id: locationId }));
  }, [canAccessBilling, dispatch, locationId]);

  useEffect(() => {
    if (skipFetch) return;
    refreshPlans();
  }, [refreshPlans, refreshOn, skipFetch]);

  const live = useMemo(
    () =>
      (subscriptions || []).filter((row) =>
        LIVE_STATUSES.includes(row?.status),
      ),
    [subscriptions],
  );
  const liveCodes = useMemo(
    () =>
      Array.from(
        new Set([
          ...live.map((row) => row.plan_code).filter(Boolean),
          ...(plans || [])
            .filter(isLivePlan)
            .map((plan) => plan.code)
            .filter(Boolean),
        ]),
      ),
    [live, plans],
  );
  const grantedCodes = useMemo(
    () =>
      Array.from(
        new Set(
          (plans || [])
            .filter((plan) => plan?.already_granted)
            .map((plan) => plan.code)
            .filter(Boolean),
        ),
      ),
    [plans],
  );

  const packageStatus = (pkg) => {
    const code = planCode(pkg);
    if (!code) return "available";
    if (isLivePlan(pkg) || liveCodes.includes(code) || grantedCodes.includes(code)) {
      return "current";
    }
    if (
      pkg?.included_in_bundle ||
      (selectionHasBundle([...liveCodes, ...grantedCodes], plans) &&
        !isBundlePlan(pkg))
    ) {
      return "included";
    }
    return "available";
  };

  const checkoutPlans = (rawCodes = []) => {
    if (!canAccessBilling) {
      dispatch(
        errorAlert(
          "You don’t have permission to buy a plan. Book a demo, or ask an administrator.",
        ),
      );
      return;
    }
    if (!locationId) return;
    const planCodes = exclusivePlanSelection(
      Array.from(
        new Set(
          (Array.isArray(rawCodes) ? rawCodes : [])
            .map((code) => planCode({ code }))
            .filter(Boolean),
        ),
      ),
      plans,
    );
    if (!planCodes.length) {
      dispatch(errorAlert("Select at least one plan to continue."));
      return;
    }
    const selected = (plans || []).filter((plan) =>
      planCodes.includes(planCode(plan)),
    );
    const needsBeds = selected.some((item) => !isPerLocationPlan(item));
    if (needsBeds && beds < 1) {
      dispatch(
        errorAlert(
          "This home has no CQC bed count, so pricing and checkout cannot start.",
        ),
      );
      return;
    }
    const origin = window.location.origin;
    if (live.length > 0 && selected.some(isBundlePlan)) {
      dispatch(
        changeBillingPlan({
          location_id: locationId,
          plan_codes: planCodes,
          return_origin: origin,
        }),
      );
      return;
    }
    const checkoutCodes = planCodes.filter((code) => {
      const plan = selected.find((item) => planCode(item) === code);
      if (!plan) return false;
      const status = packageStatus(plan);
      if (status === "current" || status === "included") return false;
      if (plan.selectable === false) return false;
      return true;
    });
    if (!checkoutCodes.length) {
      dispatch(
        errorAlert(
          selected[0]?.blocked_reason ||
            "Those plans are not available to buy right now.",
        ),
      );
      return;
    }
    dispatch(
      startBillingCheckout({
        location_id: locationId,
        plan_codes: checkoutCodes,
        return_origin: origin,
      }),
    );
  };

  const buyPackage = (pkg, extras = []) => {
    const code = planCode(pkg);
    if (!code) {
      dispatch(errorAlert("This plan is not available to buy yet."));
      return;
    }
    const extraCodes = (Array.isArray(extras) ? extras : [])
      .map(planCode)
      .filter((item) => item && item !== code);
    checkoutPlans([code, ...extraCodes]);
  };

  return {
    live,
    liveCodes,
    grantedCodes,
    plans,
    beds,
    locationId,
    busy: Boolean(isBusy),
    loading: Boolean(isLoading),
    packageStatus,
    buyPackage,
    checkoutPlans,
    canAccessBilling,
    refreshPlans,
  };
};

export default useSubscriptionPurchase;
